import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';

/**
 * OV-1405. A document ZIP too large to build while the customer waits is
 * built in the background instead: the download request answers 202 with one
 * of these, and the page polls it until the archive is ready.
 *
 * The customer is also e-mailed a link to /downloads/:id, so leaving the page
 * loses nothing.
 */
export interface DocumentZipExport {
  id: string;
  status: 'pending' | 'building' | 'ready' | 'failed' | 'expired';
  filename: string;
  estimated_bytes: number;
  /** Selected documents that were unavailable and left out of the archive. */
  skipped_count: number;
  failure_reason: 'not_servable' | 'unavailable' | null;
  ready_at: string | null;
  expires_at: string | null;
  /** Same-origin path to the archive; null until it can be downloaded. */
  download_url: string | null;
}

const POLL_INTERVAL_MS = 3000;

export const isZipExportInFlight = (zipExport?: DocumentZipExport) =>
  zipExport?.status === 'pending' || zipExport?.status === 'building';

/**
 * Finished without anything to download: failed, expired, or (briefly) past
 * its expiry. The server re-checks on every read, so this is the state the
 * page must explain rather than keep spinning on.
 */
export const isZipExportUnavailable = (zipExport?: DocumentZipExport) =>
  Boolean(zipExport) &&
  !isZipExportInFlight(zipExport) &&
  !zipExport?.download_url;

/**
 * The export a download request answered with, or null when it answered with
 * the ZIP itself (a selection small enough to build in the request).
 */
export async function readZipExport(
  response: Response
): Promise<DocumentZipExport | null> {
  if (response.status !== 202) return null;
  return (await response.json()) as DocumentZipExport;
}

/**
 * Starts the browser's own download of a finished archive. A same-origin
 * navigation rather than fetch-to-blob: the session cookie authenticates it,
 * and the browser streams the file to disk with its own progress indicator
 * instead of holding hundreds of megabytes in page memory.
 */
export function startZipDownload(downloadUrl: string) {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.rel = 'noopener';
  link.click();
}

/**
 * Polls one export until it reaches an outcome. With `autoDownload`, the
 * archive downloads by itself the moment it is ready — once per export.
 */
export function useDocumentZipExport(
  exportId: string | null | undefined,
  { autoDownload = false }: { autoDownload?: boolean } = {}
) {
  const query = useQuery({
    queryKey: ['document-zip-exports', exportId],
    queryFn: async (): Promise<DocumentZipExport> => {
      const response = await API.authenticated.fetch(
        `/document_zip_exports/${exportId}`
      );
      if (!response.ok) {
        throw new Error('Failed to load download status');
      }
      return (await response.json()) as DocumentZipExport;
    },
    enabled: Boolean(exportId),
    refetchInterval: query =>
      isZipExportInFlight(query.state.data) ? POLL_INTERVAL_MS : false,
  });

  const downloadedFor = useRef<string | null>(null);
  const downloadUrl = query.data?.download_url;

  useEffect(() => {
    if (!autoDownload || !exportId || !downloadUrl) return;
    if (downloadedFor.current === exportId) return;

    downloadedFor.current = exportId;
    startZipDownload(downloadUrl);
  }, [autoDownload, exportId, downloadUrl]);

  // A later, customer-initiated download (the button, or the e-mail link's
  // page) re-reads the status first. The server re-checks expiry and
  // withdrawals on that read, so a download it would refuse surfaces as an
  // in-app message instead of the browser navigating to an error response.
  const { refetch } = query;
  const download = useCallback(async () => {
    const { data } = await refetch();
    if (data?.download_url) startZipDownload(data.download_url);
  }, [refetch]);

  return {
    zipExport: query.data,
    isLoading: query.isLoading,
    error: query.error,
    download,
  };
}
